import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  AchievementIcon,
  AchievementUserStatus
} from 'components/achievements';
import {
  Badge,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  TitleWithIcon,
  User
} from 'components/common';
import { Box, BoxBackLink, BoxRow } from 'components/common/box';
import { ParticipantsStats } from 'components/common/stats';
import { MyTeamFollowBox, useMyTeamFollowContext, useTeamUserList } from '.';

import type { GetTeamAchievementsRes } from 'shared-types/responses';

const MyTeamAchievementsAchievement = () => {
  const { messages, user } = useReduxState();

  const { queryParams } = useMyTeamFollowContext();

  const achievementTypes = useAchievementTypes();

  const achievementId = queryParams['achievementId'] as string;

  // Fetch only on the achievement id so changing the user-list filters
  // (page/search/participantsOnly) never refetches — the single-achievement
  // response already carries every supervised user, so filtering is local.
  const { data, loading } = useApiData<GetTeamAchievementsRes>({
    url: `/admin/supervisors/${user.id}/team-achievements?achievementId=${achievementId}`,
    onlyIf: Boolean(user?.id && achievementId)
  });

  const achievement = data?.achievements?.[0];

  const isAchievementWithItems = [
    'conversation',
    'group-conversation',
    'tasklist'
  ].includes(achievement?.type);

  const { numberOfPages, participantsOnly, users } = useTeamUserList({
    isParticipant: (user) =>
      Boolean(achievement?.progressData?.progressDataByUser?.[user.id]),
    supervisedUsers: data?.supervisedUsers || []
  });

  return loading && !achievement ? (
    <LoadingIndicator />
  ) : (
    <>
      <Box
        title={
          <BoxBackLink
            as="/my-team/follow/achievements"
            href="/my-team/follow/achievements"
            text={messages.commonAllAchievements}
          />
        }
      >
        {achievement ? (
          <div className="row-sm row-center">
            <div className="col">
              <FlexContainer style={{ gap: 15, minWidth: 250 }}>
                <AchievementIcon size="small" type={achievement.type} />

                <TitleWithIcon
                  title={achievement.title}
                  titleLink={{
                    as: `/achievements/${achievement.id}`,
                    href: '/achievements/[achievementId]'
                  }}
                >
                  {achievementTypes[achievement.type]?.title}
                </TitleWithIcon>
              </FlexContainer>
            </div>
            <div className="col-min">
              <ParticipantsStats
                completionEnabled={true}
                notStartedLabel={
                  isAchievementWithItems
                    ? participantsOnly
                      ? messages.tasksUsersNotStartedSingular
                      : messages.commonNotStartedOrNotAdded
                    : messages.commonNotAdded
                }
                progressData={{
                  ...achievement.progressData,
                  // participantsOnly drops the "not added" tail, so the bar max
                  // becomes the number of participants (i.e. started).
                  participants: participantsOnly
                    ? achievement.progressData.added
                    : achievement.progressData.participants
                }}
                showInProgress={isAchievementWithItems}
                showNotStarted={isAchievementWithItems || !participantsOnly}
              />
            </div>
          </div>
        ) : null}
      </Box>

      <MyTeamFollowBox
        numberOfPages={numberOfPages}
        style={{ marginTop: 20 }}
        title={messages.commonUsers}
      >
        {loading ? (
          <LoadingIndicator />
        ) : users?.length ? (
          users.map((user) => (
            <BoxRow key={user.id}>
              <div className="row-sm row-center">
                <div className="col">
                  <User enableProfileLink={true} user={user} />
                </div>
                <div className="col-min">
                  {achievement?.progressData?.progressDataByUser?.[user.id] ? (
                    <AchievementUserStatus
                      achievementData={{
                        ...achievement.progressData.progressDataByUser[user.id],
                        numberOfCompletedTemplateItems:
                          achievement?.progressData.progressDataByUser[user.id]
                            .numberOfItemsCompleted
                      }}
                      achievementType={achievement.type}
                      numberOfItems={
                        achievement.progressData?.progressDataByUser[user.id]
                          .numberOfItems
                      }
                    />
                  ) : (
                    <Badge isRounded={false} text={messages.commonNotAdded} />
                  )}
                </div>
              </div>
            </BoxRow>
          ))
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </MyTeamFollowBox>
    </>
  );
};

export default MyTeamAchievementsAchievement;
